import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { post } from '../../api/apiService';
import { validateIndianPhone, errorStyle } from '../../utils/loginUtils';
import image from '../../assets/icons/nayonika-group.png';
import edit from '../../assets/icons/phoneno-edit.png';
import { apiEndPoints } from '../../api/apiEndPoints';
// Main Login Component
const LoginPage: React.FC = () => {
  // State management
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [sessionId, setSessionId] = useState<string>('');
  const [otpSentTo, setOtpSentTo] = useState<string>('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [resendTimer, setResendTimer] = useState<number>(30);
  const navigate = useNavigate();


  // Handle login method change (Phone number or Email Address)
  const handleLoginMethodChange = (method: 'phone' | 'email') => {
    setLoginMethod(method);
    setShowOtpInput(false);
  };
  // Handle OTP request
  const handleRequestOtp = async () => {
    if (loginMethod === 'phone' && phoneNumber) {
      setOtpSentTo(`+91 ${phoneNumber}`);

      if (!validateIndianPhone(phoneNumber)) {
        setErrorMessage('Please enter a valid mobile number');
        return;
      }

      // Reset error state
      setErrorMessage('');
      setIsSubmitting(true);

      try {
        // Simulate login API call
        const resData: any = await post_RequestOtp(phoneNumber);
        if (!resData?.sessionId) {
          throw new Error('Failed to get session ID');
        }
        setSessionId(resData.sessionId);
        setShowOtpInput(true);
        setResendTimer(30);
      } catch (error) {
        console.error('Login error:', error);

        // Handle different error types
        if (error instanceof Error) {
          if (error.message.includes('Network')) {
            setErrorMessage('Network error. Please check your internet connection and try again.');
          } else {
            setErrorMessage(error.message || 'Login failed. Please try again.');
          }
        } else {
          setErrorMessage('An unexpected error occurred');
        }
      } finally {
        setIsSubmitting(false);
      }


    } else if (loginMethod === 'email' && email) {
      setOtpSentTo(email);
      setShowOtpInput(true);
    }

  };


  const post_RequestOtp = async (phoneNumber: string) => {
    const data = {
      applicationCode: 'WEB_PORTAL',
      credential: phoneNumber,
    };
    try {
      const url = apiEndPoints.requestOTPEndPoint;
      const response = await post(url, data);
      return response;
      return response;
      // Handle success (e.g., show a success message)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to request OTP');
    }
  }

  useEffect(() => {
    if (showOtpInput && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtpInput]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {

    const value = e.target.value;
    if (isNaN(Number(value))) return;

    if (errorMessage) {
      setErrorMessage('');
    }

    const newOtp = [...otp];
    //allow only one digit
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < otp.length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleClick = (index: number) => {
    inputRefs.current[index]?.setSelectionRange(1, 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async () => {
    const combinedOtp = otp.join("");

    if (combinedOtp.length !== otp.length) {
      setErrorMessage('Please enter complete OTP');
      return;
    }

    try {
      const url = apiEndPoints.verifyOTPEndPoint;
      const data = {
        otp: combinedOtp,
        sessionId: sessionId
      };
      const resData: any = await post(url,data);

      if (!resData || !resData.token) {
        setErrorMessage('Invalid OTP. Please try again.');
        return;
      }

      // Clear error message on success
      setErrorMessage('');
      const jwtToken = resData.token;
      localStorage.setItem('jwtToken', jwtToken);
      sessionStorage.removeItem('menuSelected');
      window.location.href = "/LandingPage";

    } catch (error) {
      setErrorMessage('Invalid OTP. Please try again.');
      console.error('OTP verification error:', error);
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (showOtpInput && resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showOtpInput, resendTimer]);

  // Handle Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    try {
      const data = {
        sessionId: sessionId
      };
      const url = apiEndPoints.reSendOTPEndPoint;
      const resData: any = await post(url,data);
      if (!resData?.sessionId) {
        alert('Error resending OTP');
        return;
      }
      setSessionId(resData.sessionId);
      setShowOtpInput(true);
      setResendTimer(30);
      setOtp(['', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="flex flex-row bg-white items-center h-screen">
      {/* Logo */}
      <div className="flex items-center text-center justify-center w-[50%] h-full bg-[#F9F7ED] bg-center" style={{ backgroundSize: 'cover', backgroundImage: `url(${image})` }}>
        <div className="flex flex-row items-center justify-center">
          <img
            src="/nayonika_new.png"
            alt="Logo"
            className="w-[244px] h-[192px]"
          />
        </div>
      </div>
      {/* Login Container */}
      <div className="flex flex-col items-start justify-center w-[50%] h-full pl-12">
        <h1 className="text-3xl font-bold mb-2">Login</h1>
        <p className="text-sm font-normal text-gray-600 mb-4">Enter your details to login</p>

        {/* Login Method Tabs */}
        <div className="flex items-center justify-center mb-6">
          <button text-center
            className={`mr-6 pb-2 text-[#40559C] ${loginMethod === 'phone'
              ? 'border-b-2 border-[#40559C] font-medium text-[#40559C]'
              : 'text-gray-500'}`}
            onClick={() => handleLoginMethodChange('phone')}
          >
            Phone number
          </button>
          {/* <button 
            className={`pb-2 text-[#40559C] ${loginMethod === 'email' 
              ? 'border-b-2 border-[#40559C] font-medium text-[#40559C]' 
              : 'text-gray-500'}`}
            onClick={() => handleLoginMethodChange('email')}
          >
            Email Address
          </button> */}
        </div>

        {/* Form Content */}
        {!showOtpInput ? (
          // Step 1: Phone/Email Input
          <div className="mb-8">
            {loginMethod === 'phone' ? (
              <div className="relative">
                <input
                  type="tel"
                  maxLength={10}
                  className="w-full border rounded-full py-3 px-4 pl-12 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  placeholder="Enter mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '');
                    setPhoneNumber(newValue);
                    // Clear error message when input value changes or is cleared
                    if (errorMessage || newValue.length === 0) {
                      setErrorMessage('');
                    }
                  }}
                  onFocus={() => {
                    // Also clear error when input is focused
                    if (errorMessage) {
                      setErrorMessage('');
                    }
                  }}
                />
                <span className="absolute left-4 top-3 text-gray-600">+91</span>
              </div>
            ) : (
              <input
                type="email"
                className="w-full border rounded-full py-3 px-4 focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="Enter email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}

            {/* Request OTP Button */}
            <button
              className="w-full bg-black text-white rounded-full py-3 mt-6 hover:bg-gray-800 transition-colors"
              style={{ cursor: phoneNumber.length < 10 ? 'not-allowed' : 'pointer', backgroundColor: phoneNumber.length === 10 ? '#40559C' : '#cccccc', }}
              disabled={phoneNumber.length < 10}
              onClick={handleRequestOtp}
            >
              Request OTP
            </button>
          </div>
        ) : (
          // Step 2: OTP Verification
          <div className="mb-8">
            <p className="text-center mb-4 text-gray-600">
              Enter OTP sent to
              <span className="ml-1 text-[#40559C] font">{otpSentTo}
                <button className="ml-2"
                  onClick={() => { setShowOtpInput(false); setOtp(['', '', '', '']); }}>
                  <img src={`${edit}`}
                    alt="edit"
                    className="w-[16px] h-[16px]"
                    style={{
                      top: '488px',
                      left: '1097px'
                    }}
                  />
                </button>
              </span>
            </p>

            {/* OTP Input Fields */}
            <div className="flex justify-center space-x-4 mb-6">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  autoComplete="off"
                  className="w-12 h-12 text-center border-[1.6px] rounded-[16px] focus:outline-none focus:ring-1 focus:ring-[#40559C]-700 text-lg"
                  value={digit}
                  ref={(input) => { inputRefs.current[index] = input; }}
                  onChange={(e) => handleChange(index, e)}
                  onClick={() => handleClick(index)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>

            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}

            {/* Verify OTP Button */}
            <button
              className="w-full bg-[#40559C] text-white rounded-full py-3 mt-3 transition-colors"
              onClick={handleVerifyOtp}
            >
              Verify & Continue
            </button>

            <div className="mt-4">
              <span className="text-gray-600">Didn't receive? </span>
              <button
                className={`${resendTimer > 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[#40559C] hover:text-[#2a3a7c]'
                  } font-medium`}
                onClick={handleResendOtp}
                disabled={resendTimer > 0}
              >
                Resend OTP
              </button>
              {resendTimer > 0 && <span className="ml-1 text-gray-600">in {resendTimer} seconds</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
